'use client';

import React, { useState, useEffect } from 'react';
import ResourceCard from './ResourceCard';
import FilterSection from './FilterSection';
import NewsletterSection from './NewsletterSection';
import PremiumAccessModal from './PremiumAccessModal';
import RelatedResourcesSection from './RelatedResourcesSection';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  title: string;
  description: string;
  type: string;
  topic: string;
  image: string;
  alt: string;
  readTime: string;
  date: string;
  isPremium: boolean;
  downloadUrl?: string;
}

const ResourcesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [searchQuery, setSearchQuery] = useState('');
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showShareToast, setShowShareToast] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockResources: Resource[] = [
  {
    id: 1,
    title: 'The Complete Guide to AI Adoption in Enterprise Organizations',
    description: 'A comprehensive 50-page guide covering strategy, implementation, and change management for successful AI transformation. Includes frameworks, templates, and real-world case studies.',
    type: 'Guide',
    topic: 'AI Adoption',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b542c8d-1764670082756.png",
    alt: 'Modern office workspace with diverse team collaborating around laptop displaying AI analytics dashboard',
    readTime: '45 min read',
    date: '15 Jan 2026',
    isPremium: true
  },
  {
    id: 2,
    title: 'Webinar: Leading Change in the Age of AI - Practical Strategies',
    description: 'Join our expert panel discussion on navigating organizational change during AI implementation. Learn from leaders who have successfully transformed their organizations.',
    type: 'Webinar',
    topic: 'Change Management',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab8b7b89-1766474803574.png",
    alt: 'Professional woman presenting to engaged audience in modern conference room with large screen',
    readTime: '60 min',
    date: '10 Jan 2026',
    isPremium: false
  },
  {
    id: 3,
    title: 'Case Study: Global Retailer Achieves 40% Efficiency Gain Through AI',
    description: 'Discover how a Fortune 500 retailer successfully implemented AI across 500+ locations, achieving measurable ROI within 6 months through strategic change leadership.',
    type: 'Case Study',
    topic: 'AI Implementation',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ee94cf0-1768855842453.png",
    alt: 'Modern retail store interior with digital displays and customers using AI-powered shopping technology',
    readTime: '20 min read',
    date: '08 Jan 2026',
    isPremium: true
  },
  {
    id: 4,
    title: 'AI Readiness Assessment Template - Evaluate Your Organization',
    description: 'Free downloadable template to assess your organization\'s readiness for AI transformation. Includes scoring framework and actionable recommendations.',
    type: 'Template',
    topic: 'Organizational Readiness',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ceb05abc-1764672316473.png",
    alt: 'Business professional reviewing organizational assessment checklist on tablet with charts and graphs',
    readTime: '15 min',
    date: '05 Jan 2026',
    isPremium: false
  },
  {
    id: 5,
    title: 'Why 70% of AI Projects Fail: The Leadership Gap Nobody Talks About',
    description: 'An in-depth analysis of the critical leadership challenges that derail AI initiatives, with practical solutions for building transformation capability.',
    type: 'Article',
    topic: 'Leadership',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1da386338-1767365955857.png",
    alt: 'Diverse leadership team in strategic planning session with whiteboard showing AI transformation roadmap',
    readTime: '12 min read',
    date: '02 Jan 2026',
    isPremium: false
  },
  {
    id: 6,
    title: 'ROI Framework for AI Transformation: Measure What Matters',
    description: 'Comprehensive framework for calculating and tracking ROI throughout your AI transformation journey. Includes financial models and KPI templates.',
    type: 'Guide',
    topic: 'ROI Framework',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3895ecc-1764656426788.png",
    alt: 'Financial analyst reviewing ROI dashboard with growth charts and performance metrics on multiple screens',
    readTime: '35 min read',
    date: '28 Dec 2025',
    isPremium: true
  },
  {
    id: 7,
    title: 'Building AI-Ready Teams: Skills, Mindsets, and Organizational Culture',
    description: 'Learn how to develop the human capabilities required for successful AI adoption, from technical skills to cultural transformation.',
    type: 'Article',
    topic: 'Change Management',
    image: "https://images.unsplash.com/photo-1690192053559-6753ee4dd5e9",
    alt: 'Collaborative team workshop with diverse professionals brainstorming AI implementation strategies on sticky notes',
    readTime: '18 min read',
    date: '22 Dec 2025',
    isPremium: false
  },
  {
    id: 8,
    title: 'Webinar Replay: Overcoming Resistance to AI in Traditional Industries',
    description: 'Watch our expert panel discuss strategies for managing stakeholder concerns and building buy-in for AI initiatives in risk-averse organizations.',
    type: 'Webinar',
    topic: 'Change Management',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca30e78a-1768212805739.png",
    alt: 'Virtual webinar interface showing panel of three experts discussing AI transformation strategies',
    readTime: '55 min',
    date: '18 Dec 2025',
    isPremium: false
  },
  {
    id: 9,
    title: 'AI Implementation Playbook: From Strategy to Execution',
    description: 'Step-by-step playbook covering the entire AI implementation lifecycle, with templates, checklists, and decision frameworks for each phase.',
    type: 'Guide',
    topic: 'AI Implementation',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14e52298c-1764668075861.png",
    alt: 'Project manager reviewing detailed AI implementation timeline and milestones on large wall-mounted board',
    readTime: '40 min read',
    date: '15 Dec 2025',
    isPremium: true
  }];


  const relatedResources = [
  {
    id: 101,
    title: 'Quick Start Guide: Your First 90 Days of AI Transformation',
    type: 'Guide',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d99f36af-1764866840786.png",
    alt: 'Business calendar showing 90-day transformation roadmap with key milestones marked',
    readTime: '10 min'
  },
  {
    id: 102,
    title: 'Change Management Checklist for AI Projects',
    type: 'Template',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_149b78e0a-1769342597523.png",
    alt: 'Professional checklist template with checkboxes and action items for change management',
    readTime: '5 min'
  },
  {
    id: 103,
    title: 'Video: 5 Common AI Adoption Mistakes and How to Avoid Them',
    type: 'Webinar',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14db68f9f-1766720655754.png",
    alt: 'Video thumbnail showing speaker presenting about AI adoption challenges with warning icons',
    readTime: '25 min'
  }];


  const filteredResources = mockResources.filter((resource) => {
    const matchesType = selectedType === 'All Types' || resource.type === selectedType;
    const matchesTopic = selectedTopic === 'All Topics' || resource.topic === selectedTopic;
    const matchesSearch =
    searchQuery === '' ||
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesTopic && matchesSearch;
  });

  const handleDownload = (id: number) => {
    const resource = resources.find((r) => r.id === id);
    if (resource?.isPremium) {
      setSelectedResource(resource);
      setIsPremiumModalOpen(true);
    }
  };

  const handleShare = (id: number) => {
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 3000);
  };

  const handleClearFilters = () => {
    setSelectedType('All Types');
    setSelectedTopic('All Topics');
    setSearchQuery('');
  };

  const handleRelatedResourceClick = (id: number) => {



    // Navigate to related resource
  };if (!isHydrated) {return (
      <div className="min-h-screen bg-background">
        <div className="h-20" />
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="h-24 bg-muted rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-20" />

      <section className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Icon name="BookOpenIcon" size={32} variant="outline" className="text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold font-headline mb-6">
              AI Transformation Resources
            </h1>
            <p className="text-xl text-primary-foreground/90 mb-8">
              Practical guides, frameworks, and insights to accelerate your AI adoption journey. From strategy to execution, we share what works.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="DocumentTextIcon" size={20} variant="solid" className="text-white" />
                <span>50+ Resources</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} variant="solid" className="text-white" />
                <span>10,000+ Downloads</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={20} variant="solid" className="text-white" />
                <span>Expert Insights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <FilterSection
            selectedType={selectedType}
            selectedTopic={selectedTopic}
            searchQuery={searchQuery}
            onTypeChange={setSelectedType}
            onTopicChange={setSelectedTopic}
            onSearchChange={setSearchQuery}
            onClearFilters={handleClearFilters} />


          {filteredResources.length === 0 ?
          <div className="text-center py-16">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MagnifyingGlassIcon" size={32} variant="outline" className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold font-headline text-foreground mb-2">
                No resources found
              </h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your filters or search query to find what you're looking for.
              </p>
              <button
              onClick={handleClearFilters}
              className="px-6 py-3 bg-secondary text-secondary-foreground rounded-md font-semibold hover:bg-secondary/90 transition-all duration-300">

                Clear All Filters
              </button>
            </div> :

          <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredResources.length} of {mockResources.length} resources
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {filteredResources.map((resource) =>
              <ResourceCard
                key={resource.id}
                resource={resource}
                onDownload={handleDownload}
                onShare={handleShare} />

              )}
              </div>
            </>
          }

          <RelatedResourcesSection
            resources={relatedResources}
            onResourceClick={handleRelatedResourceClick} />

        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <NewsletterSection />
        </div>
      </section>

      <PremiumAccessModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
        resourceTitle={selectedResource?.title || ''} />


      {showShareToast &&
      <div className="fixed bottom-8 right-8 bg-success text-success-foreground px-6 py-4 rounded-lg shadow-modal flex items-center space-x-3 z-50 animate-slide-up">
          <Icon name="CheckCircleIcon" size={24} variant="solid" />
          <span className="font-medium">Link copied to clipboard!</span>
        </div>
      }
    </div>);

};

export default ResourcesInteractive;